/**
 * Whether a translation's render is actually under way.
 *
 * `run_translation()` owns the whole status lifecycle: it claims the row itself,
 * so the console never writes STARTED. A job that dies before its container
 * starts therefore leaves the row untouched and still launchable.
 *
 * What this covers is the other half — a run that claimed the row and then died
 * without writing a terminal state (killed, OOM, spot reclaim). R has an escape
 * for that: claim_translation() will take over a row whose `started_at` is older
 * than `stale_after`. But that branch can only fire if something launches a
 * second run, and the console is the only thing that launches one. So the
 * console has to stop believing STARTED at the same age R does, or the escape is
 * unreachable and the row is stuck.
 *
 * Shared by TriggerReportButton (which decides whether a launch is allowed) and
 * TranslationDetail (which decides whether the product set may be edited out
 * from under a live run), because those two answers disagreeing is what allows
 * two renders onto one artifact.
 */

/** Must match `stale_after` in claim_translation() (brms_database_helpers.R). */
export const STALE_MS = 12 * 60 * 60 * 1000;

export const RUNNING_STATUS = 'STARTED';

/**
 * Parse a `timestamp without time zone` as the UTC instant it actually holds.
 *
 * The column carries no offset and PostGraphile serialises it without one, so
 * `Date.parse('2026-09-08T15:00:00')` reads it as *local* time. The value is
 * UTC — `claim_translation()` sets it with `now()` on a UTC server session — so
 * on a browser at UTC+X that misreads it as X hours older than it is, and a
 * render that just started can be judged abandoned while it is still running.
 * At UTC+13 that is more than the whole stale window.
 *
 * Appending 'Z' when there is no designator pins it to the instant meant.
 */
const parseUtc = (value: string): number =>
  Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);

/**
 * True when a render started long enough ago that it is treated as abandoned.
 *
 * An absent or unparseable `startedAt` is never abandoned: refusing a launch is
 * recoverable, launching a second render over a live one is not. A start in the
 * future is treated the same way, for the same reason.
 */
export const isRenderAbandoned = (
  status: string,
  startedAt?: string | null,
  now: number = Date.now(),
): boolean => {
  if (status !== RUNNING_STATUS || !startedAt) return false;
  const started = parseUtc(startedAt);
  return !Number.isNaN(started) && now - started > STALE_MS;
};

/** True while a render is believed to be running, and so must not be disturbed. */
export const isRenderRunning = (
  status: string,
  startedAt?: string | null,
  now: number = Date.now(),
): boolean =>
  status === RUNNING_STATUS && !isRenderAbandoned(status, startedAt, now);
